import { useState } from 'react';
import { useSnackbar } from 'notistack';
import type { Moment } from 'moment';
import { JoinedFilters } from '../components/PromotionOperationHeader/types';
import { downloadOrdersCsv } from '../api/downloadOrdersCsv';

export const mapCsvParams = (f: JoinedFilters) => ({
  search: f.search || undefined,
  statusId: f.statusId || undefined,
  userId: f.userId || '-1',
  startDate: toBackendDate(f.startDate),
  endDate: toBackendDate(f.endDate),
  order_state_id: f.order_state_id || undefined,
  retail: f.retail || undefined,
  marketplace: f.marketplace || undefined,
  orderBy: f.orderBy || undefined,
  direction: f.direction || undefined,
  promotion: f.promotion || undefined,
});

const toBackendDate = (d?: Date | string | null | { toDate?: () => Date }) =>
  d
    ? d instanceof Date
      ? d.toString()
      : typeof d === 'string'
      ? new Date(d).toString()
      : typeof d === 'object' && typeof (d as any).toDate === 'function'
      ? (d as any).toDate().toString()
      : undefined
    : undefined;

type MaybeDate = Date | Moment | null;

interface UseExportCsvParams {
  filter: JoinedFilters;
  statusId: string;
  marketplace: string;
  userId: string;
  search: string;
  startDate: MaybeDate;
  endDate: MaybeDate;
  order_state_id: string;
  retail: string;
}

export const useExportCsv = ({
  filter,
  statusId,
  marketplace,
  userId,
  search,
  startDate,
  endDate,
  order_state_id,
  retail,
}: UseExportCsvParams) => {
  const [downloading, setDownloading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleDownloadCsv = async () => {
    try {
      setDownloading(true);

      const params = mapCsvParams({
        ...filter,
        statusId,
        marketplace,
        userId,
        search,
        startDate: startDate as any,
        endDate: endDate as any,
        order_state_id,
        retail,
      } as JoinedFilters);

      const resp = await downloadOrdersCsv(params);

      const cd = (resp.headers && (resp.headers['content-disposition'] as string)) || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match?.[1] || `orders-${new Date().toISOString().slice(0, 10)}.csv`;

      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
      enqueueSnackbar('Descarga finalizada', { variant: 'success', autoHideDuration: 5000 });
    } catch (e) {
      enqueueSnackbar('Error al descargar el archivo', { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return { downloading, handleDownloadCsv };
};
